import { inject, Injectable } from '@angular/core';

import { map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  BrandWifiProfileConfigurationService,
  KioskWifiProfileConfigurationService,
  LocationWifiProfileConfigurationService,
  ModelNameFlags,
  RoomWifiProfileConfigurationService,
} from '@era/settings-sdk';
import { EnvironmentService } from '@methylium/core';
import { Observable } from 'rxjs';
import { safeMap } from '../effects.helpers';
import {
  CategoryWifiProfileApiActions,
  CategoryWifiProfileEffectActions,
  CategoryWifiProfileResolverActions,
  CategoryWifiProfileViewActions,
} from './category-wifi-profile-configuration.actions';
import { WifiProfileConfiguration, wifiProfileConfigurationFrom } from './category-wifi-profile-configuration.state';
import { Category } from '../router/category';

@Injectable()
export class CategoryWifiProfileEffects {
  protected features = inject(EnvironmentService).environment.features;

  constructor(
    private readonly actions: Actions,
    private readonly brandWifiProfileService: BrandWifiProfileConfigurationService,
    private readonly locationWifiProfileService: LocationWifiProfileConfigurationService,
    private readonly roomWifiProfileService: RoomWifiProfileConfigurationService,
    private readonly kioskWifiProfileService: KioskWifiProfileConfigurationService
  ) {}

  /**
   * The category wifi profile configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   */
  categoryWifiProfileLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryWifiProfileViewActions.wifiProfileConfigurationsUpdated),
      mergeMap((action) =>
        safeMap(
          this.categoryWifiProfileLinkUpdatedEffectBuilder(action),
          (updatedWifiProfiles) =>
            CategoryWifiProfileApiActions.categoryWifiProfileConfigurationsUpdated({
              category: action.category,
              wifiProfiles: updatedWifiProfiles,
            }),
          (error) =>
            CategoryWifiProfileApiActions.categoryWifiProfileConfigurationsUpdatedError({
              category: action.category,
              error,
            })
        )
      )
    );
  });

  /**
   * The (re)load of the category wifi profiles has been requested.
   * Effect: we load the state from the api.
   */
  categoryWifiProfilesRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        CategoryWifiProfileResolverActions.categoryWifiProfilesRequested,
        CategoryWifiProfileEffectActions.categoryWifiProfilesRequested
      ),
      mergeMap((action) =>
        safeMap(
          this.categoryWifiProfilesRequestedEffectBuilder(action),
          (categoryWifiProfiles) =>
            CategoryWifiProfileApiActions.categoryWifiProfilesLoaded({
              wifiProfiles: categoryWifiProfiles,
            }),
          (error) =>
            CategoryWifiProfileApiActions.categoryWifiProfilesLoadedError({
              error,
            })
        )
      )
    );
  });

  /**
   * The wifi profiles couldn't be updated.
   * Effect: we request a reload of the state from the api.
   */
  categoryWifiProfilesUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryWifiProfileApiActions.categoryWifiProfileConfigurationsUpdatedError),
      map((action) =>
        CategoryWifiProfileEffectActions.categoryWifiProfilesRequested({
          category: action.category,
          modelNames: [ModelNameFlags.All],
        })
      )
    );
  });

  private categoryWifiProfileLinkUpdatedEffectBuilder(action: {
    category: Category;
    updatedWifiProfilesLinkedToCategory: WifiProfileConfiguration[];
  }): Observable<WifiProfileConfiguration[]> {
    const command = {
      wifiProfilePresets: action.updatedWifiProfilesLinkedToCategory.map((x) => ({
        wifiProfilePresetId: x.profileId,
        models: x.modelNames,
      })),
    };

    switch (action.category.categoryType) {
      case 'Brand':
        return this.brandWifiProfileService
          .putWifiProfileToBrand(action.category.categoryId, command)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      case 'Location':
        return this.locationWifiProfileService
          .putWifiProfileToLocation(action.category.categoryId, command)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      case 'Room':
        return this.roomWifiProfileService
          .putWifiProfileToRoom(action.category.categoryId, command)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      case 'Kiosk':
        return this.kioskWifiProfileService
          .putWifiProfileToKiosk(action.category.categoryId, command)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      default:
        throw new Error(`Unsupported category type: ${action.category.categoryType}`);
    }
  }

  private categoryWifiProfilesRequestedEffectBuilder(action: {
    category: Category;
    modelNames: ModelNameFlags[];
  }): Observable<WifiProfileConfiguration[]> {
    switch (action.category.categoryType) {
      case 'Brand':
        return this.brandWifiProfileService
          .getWifiProfilesForBrand(action.category.categoryId, action.modelNames)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      case 'Location':
        return this.locationWifiProfileService
          .getWifiProfilesForLocation(action.category.categoryId, action.modelNames)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      case 'Room':
        return this.roomWifiProfileService
          .getWifiProfilesForRoom(action.category.categoryId, action.modelNames)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      case 'Kiosk':
        return this.kioskWifiProfileService
          .getWifiProfilesForKiosk(action.category.categoryId, action.modelNames)
          .pipe(map((configurations) => configurations.map(wifiProfileConfigurationFrom)));
      default:
        throw new Error(`Unsupported category type: ${action.category.categoryType}`);
    }
  }
}
